import { useState, useEffect, useCallback } from "react";
import { Search, Filter, AlertTriangle, Shield, Eye, Clock, ChevronLeft, ChevronRight } from "lucide-react";
import api from "../../services/api";
import VigiaEventDetail from "./VigiaEventDetail";

var SEV_COLORS = {
  critical: { bg: "bg-red-100", text: "text-red-700", dot: "bg-red-500" },
  high: { bg: "bg-orange-100", text: "text-orange-700", dot: "bg-orange-500" },
  medium: { bg: "bg-amber-100", text: "text-amber-700", dot: "bg-amber-500" },
  low: { bg: "bg-gray-100", text: "text-gray-600", dot: "bg-gray-400" },
};

var STATUS_LABELS = { open: "Aberto", acknowledged: "Reconhecido", resolved: "Resolvido", muted: "Silenciado" };
var STATUS_COLORS = {
  open: "bg-red-100 text-red-700",
  acknowledged: "bg-blue-100 text-blue-700",
  resolved: "bg-green-100 text-green-700",
  muted: "bg-gray-100 text-gray-500",
};

function formatDate(d) {
  if (!d) return "-";
  try { return new Date(d).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); }
  catch(e) { return "-"; }
}

export default function VigiaEventList() {
  var eventsState = useState([]);
  var events = eventsState[0];
  var setEvents = eventsState[1];
  var statsState = useState({});
  var stats = statsState[0];
  var setStats = statsState[1];
  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];
  var filtersState = useState({ severity: "", status: "", source: "", search: "" });
  var filters = filtersState[0];
  var setFilters = filtersState[1];
  var pageState = useState(1);
  var page = pageState[0];
  var setPage = pageState[1];
  var totalState = useState(0);
  var total = totalState[0];
  var setTotal = totalState[1];
  var selectedState = useState(null);
  var selected = selectedState[0];
  var setSelected = selectedState[1];

  var fetchEvents = useCallback(async function() {
    setLoading(true);
    try {
      var params = new URLSearchParams();
      params.append("page", page);
      params.append("limit", "25");
      if (filters.severity) params.append("severity", filters.severity);
      if (filters.status) params.append("status", filters.status);
      if (filters.source) params.append("source", filters.source);
      if (filters.search) params.append("search", filters.search);
      var res = await api.get("/vigia/events?" + params.toString());
      setEvents(res.data.events || []);
      setTotal(res.data.total || 0);
      setStats(res.data.stats || {});
    } catch(err) { console.error("Erro vigia:", err); }
    finally { setLoading(false); }
  }, [page, filters]);

  useEffect(function() { fetchEvents(); }, [fetchEvents]);

  function handleFilter(key, value) {
    setFilters(function(p) { var n = Object.assign({}, p); n[key] = value; return n; });
    setPage(1);
  }

  return (
    <div data-testid="vigia-events-list">
      {selected && (
        <VigiaEventDetail
          eventId={selected}
          onClose={function() { setSelected(null); }}
          onAction={function() { setSelected(null); fetchEvents(); }}
        />
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <StatCard label="Abertos" value={stats.open || 0} color="text-red-600" icon={AlertTriangle} />
        <StatCard label="Criticos" value={stats.critical || 0} color="text-red-700" icon={Shield} />
        <StatCard label="Altos" value={stats.high || 0} color="text-orange-600" icon={AlertTriangle} />
        <StatCard label="Resolvidos Hoje" value={stats.resolved_today || 0} color="text-green-600" icon={Eye} />
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
          <div className="relative flex-1 w-full">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por rota, modulo, mensagem..."
              value={filters.search}
              onChange={function(e) { handleFilter("search", e.target.value); }}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#3B82F6]"
              data-testid="vigia-search"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            <select value={filters.severity} onChange={function(e) { handleFilter("severity", e.target.value); }} className="px-3 py-2 text-xs border rounded-lg" data-testid="vigia-filter-severity">
              <option value="">Severidade</option>
              <option value="critical">Critico</option>
              <option value="high">Alto</option>
              <option value="medium">Medio</option>
              <option value="low">Baixo</option>
            </select>
            <select value={filters.status} onChange={function(e) { handleFilter("status", e.target.value); }} className="px-3 py-2 text-xs border rounded-lg" data-testid="vigia-filter-status">
              <option value="">Status</option>
              <option value="open">Aberto</option>
              <option value="acknowledged">Reconhecido</option>
              <option value="resolved">Resolvido</option>
              <option value="muted">Silenciado</option>
            </select>
            <select value={filters.source} onChange={function(e) { handleFilter("source", e.target.value); }} className="px-3 py-2 text-xs border rounded-lg" data-testid="vigia-filter-source">
              <option value="">Origem</option>
              <option value="backend">Backend</option>
              <option value="frontend">Frontend</option>
              <option value="integration">Integracao</option>
              <option value="database">Banco</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-6 h-6 border-2 border-[#3B82F6] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-12">
            <Shield className="w-10 h-10 text-green-300 mx-auto mb-2" />
            <p className="text-sm text-gray-400">Nenhum evento encontrado</p>
          </div>
        ) : (
          <div>
            {events.map(function(ev) {
              var sev = SEV_COLORS[ev.severity] || SEV_COLORS.medium;
              var stCls = STATUS_COLORS[ev.status] || STATUS_COLORS.open;
              var stLabel = STATUS_LABELS[ev.status] || ev.status;
              return (
                <div
                  key={ev.id}
                  className="px-4 py-3 border-b border-gray-50 hover:bg-gray-50/50 cursor-pointer transition-colors"
                  onClick={function() { setSelected(ev.id); }}
                  data-testid={"vigia-event-" + ev.id}
                >
                  <div className="flex items-start gap-3">
                    <div className={"w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0 " + sev.dot} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={"px-2 py-0.5 text-[10px] font-bold uppercase rounded " + sev.bg + " " + sev.text}>{ev.severity}</span>
                        <span className={"px-2 py-0.5 text-[10px] font-medium rounded " + stCls}>{stLabel}</span>
                        <span className="text-[10px] text-gray-400 font-mono">{ev.source}</span>
                      </div>
                      <p className="text-sm font-medium text-[#0A2F3D] mt-1 truncate">{ev.title}</p>
                      <div className="flex items-center gap-4 mt-1 text-xs text-gray-400">
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatDate(ev.last_seen_at)}</span>
                        <span className="font-medium">{ev.occurrence_count}x</span>
                        {ev.endpoint && <span className="truncate max-w-[200px] font-mono">{ev.endpoint}</span>}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {total > 25 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
            <p className="text-xs text-gray-400">{total} eventos</p>
            <div className="flex gap-2">
              <button disabled={page <= 1} onClick={function() { setPage(page - 1); }} className="p-1.5 rounded-lg border hover:bg-gray-50 disabled:opacity-30">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-xs py-1.5 px-2 text-gray-500">Pag. {page}</span>
              <button disabled={page * 25 >= total} onClick={function() { setPage(page + 1); }} className="p-1.5 rounded-lg border hover:bg-gray-50 disabled:opacity-30">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, color, icon: Icon }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="flex items-center gap-2 mb-1">
        <Icon className={"w-4 h-4 " + color} />
        <span className="text-xs text-gray-400">{label}</span>
      </div>
      <p className={"text-2xl font-bold " + color}>{value}</p>
    </div>
  );
}
